import { useEffect, useRef } from 'react';

const PRINT_STYLESHEET_LOAD_TIMEOUT = 10_000;

/** Resolves when a stylesheet has loaded, failed, or taken too long to load. */
const waitForStylesheet = (stylesheet: HTMLLinkElement) =>
  new Promise<void>(resolve => {
    const timeoutId = window.setTimeout(resolve, PRINT_STYLESHEET_LOAD_TIMEOUT);
    const finish = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };

    stylesheet.addEventListener('error', finish, { once: true });
    stylesheet.addEventListener('load', finish, { once: true });
  });

/** Copies CSS rules from a loaded stylesheet, including Emotion's production CSSOM rules. */
const copyStylesheetRules = (
  printDocument: Document,
  stylesheet: CSSStyleSheet,
) => {
  try {
    const printStyle = printDocument.createElement('style');
    printStyle.textContent = Array.from(
      stylesheet.cssRules,
      rule => rule.cssText,
    ).join('\n');
    printDocument.head.append(printStyle);
    return true;
  } catch {
    return false;
  }
};

/** Returns the link that can load a stylesheet whose rules cannot be read directly. */
const getStylesheetLink = (stylesheet: CSSStyleSheet) =>
  stylesheet.ownerNode instanceof HTMLLinkElement
    ? stylesheet.ownerNode
    : undefined;

/** Preserves the source document as the base for any relative asset URLs in the clone. */
const addPrintDocumentBaseUrl = (printDocument: Document) => {
  const base = printDocument.createElement('base');
  base.href = document.baseURI;
  printDocument.head.append(base);
};

const copyPrintStyles = (printDocument: Document) => {
  const stylesheetLoads: Array<Promise<void>> = [];

  for (const stylesheet of Array.from(document.styleSheets)) {
    if (copyStylesheetRules(printDocument, stylesheet)) continue;

    const stylesheetLink = getStylesheetLink(stylesheet);
    if (!stylesheetLink) continue;

    const printStylesheet = printDocument.importNode(stylesheetLink, true);
    printStylesheet.href = stylesheetLink.href;
    stylesheetLoads.push(waitForStylesheet(printStylesheet));
    printDocument.head.append(printStylesheet);
  }

  return Promise.all(stylesheetLoads);
};

const createPrintFrame = () => {
  const printFrame = document.createElement('iframe');
  printFrame.setAttribute('aria-hidden', 'true');
  printFrame.style.border = '0';
  printFrame.style.height = '0';
  printFrame.style.position = 'fixed';
  printFrame.style.width = '0';
  document.body.append(printFrame);
  return printFrame;
};

/** Uses the standard A4 portrait page while giving the artifact the full printable width. */
const addPrintPageLayout = (printDocument: Document) => {
  const printStyle = printDocument.createElement('style');
  printStyle.textContent = `
    @page { size: A4 portrait; margin: 12mm; }

    @media print {
      html,
      body {
        display: block !important;
        width: auto !important;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
        position: static !important;
      }

      body > section {
        display: block !important;
        flex: none !important;
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
      }

      table {
        break-inside: auto !important;
      }

      thead {
        display: table-header-group;
      }
    }
  `;
  printDocument.head.append(printStyle);
};

const initializePrintDocument = (
  printDocument: Document,
  artifactDocument: HTMLElement,
) => {
  printDocument.open();
  printDocument.write('<!doctype html><html><head></head><body></body></html>');
  printDocument.title = artifactDocument.getAttribute('aria-label') ?? 'Artifact';
  addPrintDocumentBaseUrl(printDocument);
  const stylesReady = copyPrintStyles(printDocument);
  addPrintPageLayout(printDocument);
  printDocument.body.append(artifactDocument.cloneNode(true));
  printDocument.close();

  return stylesReady;
};

/** Waits for the isolated document's styling and fonts before opening its native print dialog. */
const printFrameDocument = async (
  printFrame: HTMLIFrameElement,
  stylesReady: Promise<Array<void>>,
) => {
  const printDocument = printFrame.contentDocument;
  const printWindow = printFrame.contentWindow;
  if (!printDocument || !printWindow) {
    printFrame.remove();
    return;
  }

  printWindow.addEventListener('afterprint', () => printFrame.remove(), {
    once: true,
  });
  await stylesReady;
  await printDocument.fonts.ready;
  printWindow.focus();
  printWindow.print();
};

const isArtifactPrintShortcut = (event: KeyboardEvent) =>
  !event.repeat
  && (event.metaKey || event.ctrlKey)
  && event.key.toLowerCase() === 'p';

/** Creates an isolated print frame for the supplied artifact document. */
const printArtifactDocument = (artifactDocument: HTMLElement) => {
  const printFrame = createPrintFrame();
  const printDocument = printFrame.contentDocument;
  if (!printDocument) {
    printFrame.remove();
    return;
  }

  const stylesReady = initializePrintDocument(printDocument, artifactDocument);
  void printFrameDocument(printFrame, stylesReady);
};

/** Supplies a ref and action that print only the artifact document, outside the app layout. */
export const useArtifactDocumentPrint = () => {
  const artifactDocumentRef = useRef<HTMLElement>(null);
  const printCurrentArtifactDocument = () => {
    if (artifactDocumentRef.current) {
      printArtifactDocument(artifactDocumentRef.current);
    }
  };

  useEffect(() => {
    const handlePrintShortcut = (event: KeyboardEvent) => {
      if (!isArtifactPrintShortcut(event) || !artifactDocumentRef.current) {
        return;
      }
      event.preventDefault();
      printArtifactDocument(artifactDocumentRef.current);
    };

    window.addEventListener('keydown', handlePrintShortcut);
    return () => window.removeEventListener('keydown', handlePrintShortcut);
  }, []);

  return {
    artifactDocumentRef,
    printArtifactDocument: printCurrentArtifactDocument,
  };
};
